import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { governanceApi, formatDateTime, humanizeEvent } from "./api";
import "./TrailTimeline.css";

function actorName(actor) {
  if (!actor) return "System";
  if (typeof actor === "string") return actor;
  return actor.name || "System";
}

function dataChips(data) {
  if (!data || typeof data !== "object") return [];
  return Object.entries(data)
    .filter(
      ([, v]) =>
        v !== null &&
        v !== undefined &&
        (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    )
    .slice(0, 4)
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${String(v)}`);
}

/**
 * Audit timeline for a governance object (GET /api/governance/trail).
 * Pass `refreshKey` to re-fetch after a mutation.
 */
export default function TrailTimeline({ targetId, refreshKey = 0 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!targetId) return undefined;
    let alive = true;
    governanceApi
      .trail(targetId)
      .then((d) => {
        if (alive) setItems(d.items || []);
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, [targetId, refreshKey]);

  return (
    <section className="vsgov-trail">
      <h2 className="vsgov-trail-title">
        <History size={15} /> Audit trail
      </h2>
      {items === null && <div className="vsgov-trail-muted">Loading…</div>}
      {items !== null && items.length === 0 && (
        <div className="vsgov-trail-muted">No recorded events yet.</div>
      )}
      {items !== null && items.length > 0 && (
        <ol className="vsgov-trail-list">
          {items.map((ev, i) => (
            <li key={`${ev.event_type}-${ev.created_at}-${i}`} className="vsgov-trail-item">
              <span className="vsgov-trail-dot" aria-hidden="true" />
              <div className="vsgov-trail-body">
                <div className="vsgov-trail-line">
                  <span className="vsgov-trail-event">
                    {humanizeEvent(ev.event_type)}
                  </span>
                  <span className="vsgov-trail-actor">
                    by {actorName(ev.actor)}
                  </span>
                </div>
                {dataChips(ev.data).length > 0 && (
                  <div className="vsgov-trail-chips">
                    {dataChips(ev.data).map((c) => (
                      <span key={c} className="vsgov-trail-chip">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
                <div className="vsgov-trail-when">
                  {formatDateTime(ev.created_at)}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
